/**
 * Workflow Guidance System
 * Provides comprehensive guidance for company-wide workflows
 * Similar structure to actionGuidance.js but for workflow steps
 */

// Workflow-level guidance (for overview/summary)
export const workflowTypeGuidance = {
  recruitment: {
    importance: "Critical for building strong teams and ensuring organizational success. Poor recruitment leads to costly turnover, reduced productivity, and cultural misalignment.",
    whatToLookFor: [
      "Clear job requirements aligned with business needs",
      "Diverse candidate pools to avoid discrimination claims",
      "Consistent interview processes across all candidates",
      "Proper documentation at every stage",
      "Timely communication with candidates",
      "Compliance with employment law and data protection"
    ],
    bestPractices: [
      "Use structured interviews with standardized questions",
      "Involve multiple stakeholders in decision-making",
      "Check references and conduct background checks thoroughly",
      "Provide positive candidate experience throughout",
      "Document reasons for hiring decisions",
      "Ensure diversity and inclusion in recruitment process"
    ],
    commonPitfalls: [
      "Rushing the process and making poor hires",
      "Inconsistent interview questions leading to bias",
      "Failing to check references properly",
      "Poor communication causing candidate drop-off",
      "Not documenting selection decisions",
      "Discriminatory practices in screening or interviewing"
    ],
    legalConsiderations: [
      "Equality Act 2010 - avoid discrimination",
      "GDPR - handle candidate data properly",
      "Right to Work checks required before employment",
      "Avoid questions about protected characteristics",
      "Document objective selection criteria",
      "Provide feedback if requested"
    ]
  },

  onboarding: {
    importance: "Effective onboarding increases retention by 82% and productivity by 70%. First impressions matter - poor onboarding leads to early turnover and disengagement.",
    whatToLookFor: [
      "Smooth pre-start preparation and communication",
      "Proper equipment and access ready for day one",
      "Structured introduction to team and company culture",
      "Clear role expectations and objectives",
      "Regular check-ins during probation period",
      "Early identification of any issues or concerns"
    ],
    bestPractices: [
      "Start onboarding before day one with welcome pack",
      "Assign a buddy or mentor for support",
      "Create structured 30-60-90 day plan",
      "Schedule regular check-ins with manager",
      "Provide comprehensive training on systems and processes",
      "Gather feedback to improve onboarding process"
    ],
    commonPitfalls: [
      "No preparation before start date",
      "Overwhelming new hire with too much information",
      "Lack of clear objectives or expectations",
      "Insufficient support or guidance",
      "No structured check-ins during probation",
      "Failing to address concerns early"
    ],
    legalConsiderations: [
      "Right to Work documentation must be completed",
      "Probation period terms must be clear in contract",
      "Health and safety induction is legal requirement",
      "Data protection training for GDPR compliance",
      "Fair probation review process required",
      "Document any performance concerns during probation"
    ]
  },

  performance: {
    importance: "Regular performance management drives productivity, engagement, and development. Poor performance management leads to disengagement, unfair treatment claims, and lost productivity.",
    whatToLookFor: [
      "Clear, measurable objectives aligned to business goals",
      "Regular feedback throughout the year, not just at review",
      "Honest, constructive conversations about performance",
      "Development opportunities identified and actioned",
      "Consistency in how performance is assessed",
      "Documentation of discussions and agreements"
    ],
    bestPractices: [
      "Set SMART objectives collaboratively",
      "Provide continuous feedback, not just annual reviews",
      "Focus on development, not just assessment",
      "Use objective data and examples",
      "Involve employees in their own development planning",
      "Calibrate ratings across teams for fairness"
    ],
    commonPitfalls: [
      "Only discussing performance at annual review",
      "Vague or unmeasurable objectives",
      "Recency bias - only remembering recent performance",
      "Avoiding difficult conversations",
      "Inconsistent standards across teams",
      "No follow-through on development plans"
    ],
    legalConsiderations: [
      "Performance ratings can be evidence in tribunal claims",
      "Must be fair and non-discriminatory",
      "Document performance discussions thoroughly",
      "Link performance issues to capability procedures if needed",
      "Ensure consistency to avoid discrimination claims",
      "Consider reasonable adjustments for disabilities"
    ]
  },

  training: {
    importance: "Training and development improves performance, engagement, and retention. Lack of training leads to skill gaps, errors, and potential legal/compliance issues.",
    whatToLookFor: [
      "Clear training needs identified through analysis",
      "Training content aligned to business objectives",
      "Effective delivery methods for target audience",
      "Engagement and participation during training",
      "Knowledge transfer and application on the job",
      "Measurable improvement in performance or skills"
    ],
    bestPractices: [
      "Conduct thorough training needs analysis first",
      "Use variety of learning methods (not just classroom)",
      "Pilot test training before full rollout",
      "Provide resources for ongoing reference",
      "Measure effectiveness through assessment and feedback",
      "Follow up to ensure learning is applied"
    ],
    commonPitfalls: [
      "Training for training's sake without clear purpose",
      "One-size-fits-all approach ignoring different needs",
      "No assessment of learning or effectiveness",
      "Failing to provide time for training",
      "No follow-up or reinforcement after training",
      "Poor quality content or delivery"
    ],
    legalConsiderations: [
      "Health and safety training is legal requirement",
      "Mandatory training (e.g., data protection, equality) must be completed",
      "Keep records of training completion",
      "Ensure equal access to development opportunities",
      "Consider reasonable adjustments for disabled learners",
      "Training time should be paid working time"
    ]
  },

  policy: {
    importance: "Clear policies set expectations, ensure consistency, and protect the organization legally. Poor or outdated policies create confusion and legal risk.",
    whatToLookFor: [
      "Policy addresses genuine business need",
      "Clear, understandable language (not legalese)",
      "Compliance with current employment law",
      "Consistency with other policies and practices",
      "Stakeholder buy-in and understanding",
      "Effective communication and implementation"
    ],
    bestPractices: [
      "Involve stakeholders in policy development",
      "Get legal review before implementation",
      "Use plain English, avoid jargon",
      "Provide training on new or updated policies",
      "Make policies easily accessible to all staff",
      "Review and update policies regularly"
    ],
    commonPitfalls: [
      "Copying policies from internet without customization",
      "Overly complex or legalistic language",
      "Policies that contradict each other",
      "No training or communication on new policies",
      "Policies not followed in practice",
      "Outdated policies not reflecting current law"
    ],
    legalConsiderations: [
      "Policies must comply with employment law",
      "Contractual policies are harder to change",
      "Non-contractual policies allow more flexibility",
      "Policies can be used as evidence in tribunals",
      "Must follow own policies or risk unfair treatment claims",
      "Consult on significant policy changes"
    ]
  },

  compliance: {
    importance: "Regular compliance audits prevent legal breaches, fines, and reputational damage. Non-compliance can result in prosecution, penalties, and loss of licenses.",
    whatToLookFor: [
      "Clear scope covering all relevant compliance areas",
      "Thorough data collection and evidence gathering",
      "Honest assessment of gaps and risks",
      "Practical remediation plans with clear ownership",
      "Timely implementation of corrective actions",
      "Follow-up to ensure sustained compliance"
    ],
    bestPractices: [
      "Use compliance checklists and frameworks",
      "Involve subject matter experts",
      "Be honest about gaps - don't hide issues",
      "Prioritize remediation based on risk",
      "Set realistic timescales for improvements",
      "Document everything for audit trail"
    ],
    commonPitfalls: [
      "Superficial audits that miss real issues",
      "Identifying problems but not fixing them",
      "Lack of senior leadership support",
      "Insufficient resources for remediation",
      "No follow-up to verify improvements",
      "Treating compliance as tick-box exercise"
    ],
    legalConsiderations: [
      "Many compliance requirements are legal obligations",
      "Failure to comply can result in fines or prosecution",
      "Directors can be personally liable for some breaches",
      "Audit findings can be disclosed in legal proceedings",
      "Must act on identified compliance gaps",
      "Keep audit records for required retention periods"
    ]
  }
};

// Step-level guidance for each workflow type
export const workflowStepGuidance = {
  // RECRUITMENT WORKFLOW
  recruitment: {
    "Job Analysis & Requirements": {
      guidance: `**Purpose:** Define exactly what the role needs to deliver business objectives.

**Key Activities:**
• Review business needs and team structure
• Analyze current role (if replacement)
• Identify essential vs. desirable requirements
• Define key responsibilities and accountabilities
• Determine required skills, experience, and qualifications
• Consider future needs, not just current gaps

**Outputs:**
• Job description
• Person specification
• Competency framework
• Salary range and benefits package

**Stakeholder Involvement:**
• Line manager (primary)
• Team members
• Senior leadership
• HR/recruitment specialist`,
      tips: [
        "Focus on outcomes, not just tasks",
        "Be realistic about requirements - don't ask for unicorns",
        "Consider diversity in requirements (avoid unnecessary barriers)",
        "Think about career progression and development"
      ],
      warnings: [
        "Overly specific requirements can limit diversity",
        "Don't copy old job descriptions without review",
        "Avoid age-related or discriminatory requirements",
        "Unrealistic requirements reduce candidate pool"
      ]
    },

    "Job Advertisement Creation": {
      guidance: `**Purpose:** Create compelling advert that attracts right candidates.

**Key Elements:**
• Engaging job title (searchable, not internal jargon)
• Compelling summary of role and opportunity
• Key responsibilities (5-7 bullet points)
• Essential and desirable requirements
• What makes your organization attractive
• Clear application process and deadline
• Salary range (increasingly expected)
• Commitment to diversity and inclusion

**Tone:**
• Professional but authentic
• Inclusive and welcoming
• Focused on opportunity, not just requirements
• Honest about role and organization

**Legal Requirements:**
• No discriminatory language
• Accurate representation of role
• Comply with advertising standards`,
      tips: [
        "Use inclusive language (avoid gendered terms)",
        "Highlight development opportunities",
        "Be transparent about salary to save time",
        "Sell the opportunity, not just list requirements"
      ],
      warnings: [
        "Avoid jargon that excludes candidates",
        "Don't oversell - be honest about challenges",
        "Check for unconscious bias in language",
        "Ensure advert reflects actual role"
      ]
    },

    "Multi-Channel Posting": {
      guidance: `**Purpose:** Reach diverse candidate pool through multiple channels.

**Channels to Consider:**
• Company website/careers page
• General job boards (Indeed, Reed, etc.)
• Specialist job boards for your sector
• LinkedIn and professional networks
• Social media (Twitter, Facebook, Instagram)
• Internal job board (for internal candidates)
• University careers services (for graduates)
• Diversity-focused job boards
• Employee referral program

**Strategy:**
• Tailor message to each channel
• Use appropriate keywords for searchability
• Track which channels generate best candidates
• Consider paid promotion for hard-to-fill roles

**Budget:**
• Some channels are free (company website, LinkedIn)
• Others require payment (job boards, advertising)
• Consider cost per quality applicant, not just cost`,
      tips: [
        "Post internally first if required by policy",
        "Use employee networks for referrals",
        "Target channels where ideal candidates are",
        "Track source of applications for future planning"
      ],
      warnings: [
        "Don't rely on single channel - limits diversity",
        "Ensure all channels link to same application process",
        "Monitor and respond to questions on all channels",
        "Be consistent in messaging across channels"
      ]
    },

    "Application Collection Period": {
      guidance: `**Purpose:** Allow sufficient time for quality candidates to apply.

**Duration:**
• Typical: 2-4 weeks
• Senior roles: 4-6 weeks
• Hard-to-fill: May need longer
• Urgent roles: Minimum 1 week

**During This Period:**
• Monitor application flow
• Respond to candidate queries promptly
• May start early screening if high volume
• Track diversity of applicant pool
• Extend deadline if insufficient applications

**Candidate Experience:**
• Acknowledge all applications
• Provide realistic timescale for next steps
• Keep candidates informed of process
• Make application process user-friendly

**Data Protection:**
• Store applications securely
• Only collect necessary information
• Comply with GDPR requirements
• Have retention policy for unsuccessful applications`,
      tips: [
        "Set clear deadline and stick to it",
        "Monitor quality and quantity of applications",
        "Be responsive to candidate questions",
        "Consider extending if pool is weak"
      ],
      warnings: [
        "Don't close too early - may miss great candidates",
        "Leaving open too long creates poor candidate experience",
        "Must handle candidate data in compliance with GDPR",
        "Acknowledge all applications promptly"
      ]
    },

    "Initial Screening": {
      guidance: `**Purpose:** Identify candidates who meet essential requirements.

**Screening Criteria:**
• Essential requirements from person specification
• Relevant experience and skills
• Qualifications (if genuinely required)
• Right to work (if stated as essential)
• Availability and notice period

**Process:**
• Use consistent criteria for all candidates
• Score against person specification
• Involve multiple reviewers to reduce bias
• Focus on evidence, not assumptions
• Shortlist 5-8 candidates for interview

**Avoid:**
• Screening out based on protected characteristics
• Making assumptions from names, addresses, etc.
• Requiring unnecessary qualifications
• Unconscious bias (e.g., similar background to you)

**Documentation:**
• Record screening decisions and rationale
• Keep for at least 6 months
• May be needed if discrimination claim`,
      tips: [
        "Use blind screening where possible (remove names)",
        "Focus on evidence of skills, not just keywords",
        "Give benefit of doubt if unclear - invite to interview",
        "Involve diverse panel in screening"
      ],
      warnings: [
        "Unconscious bias is major risk at this stage",
        "Don't screen out candidates who could do job with training",
        "Avoid assumptions based on career gaps",
        "Must be able to justify all screening decisions"
      ]
    },

    "Interview Process": {
      guidance: `**Purpose:** Assess candidates fairly against role requirements.

**Interview Structure:**
• Structured interviews with same questions for all
• Competency-based questions with STAR format
• Technical assessment if relevant
• Culture fit (but avoid bias)
• Opportunity for candidate questions
• Typically 2 stages (first interview, then final)

**Interview Panel:**
• Include diverse panel members
• Train interviewers on fair interviewing
• Assign roles (lead, note-taker, etc.)
• Calibrate scoring after interviews

**Candidate Experience:**
• Provide clear information about process
• Allow reasonable adjustments if needed
• Be punctual and professional
• Provide feedback if requested

**Legal Compliance:**
• Don't ask about protected characteristics
• Focus on ability to do the job
• Allow reasonable adjustments for disabilities
• Document interview notes and scores`,
      tips: [
        "Use same questions for all candidates",
        "Take detailed notes during interview",
        "Score immediately after each interview",
        "Provide positive candidate experience"
      ],
      warnings: [
        "Avoid questions about age, marital status, children, etc.",
        "Don't make assumptions based on appearance",
        "Inconsistent questions can indicate discrimination",
        "Poor candidate experience damages employer brand"
      ]
    },

    "Reference & Background Checks": {
      guidance: `**Purpose:** Verify candidate information and suitability.

**References:**
• Request 2 references (usually including current/recent employer)
• Ask specific questions about performance, attendance, conduct
• Verify employment dates and job title
• Ask if they would re-employ the candidate
• Follow up on any concerns

**Background Checks:**
• Right to Work check (legal requirement)
• DBS check if required for role
• Qualification verification if essential
• Credit check only if relevant to role
• Social media check (be careful of discrimination)

**Timing:**
• Usually after conditional offer
• Before final offer and start date
• Allow time for checks to complete

**Data Protection:**
• Get candidate consent for checks
• Only check what's relevant to role
• Handle information securely
• Comply with GDPR`,
      tips: [
        "Always check references, don't skip this step",
        "Ask specific questions, not just 'are they good?'",
        "Follow up on any red flags or concerns",
        "Verify right to work before employment starts"
      ],
      warnings: [
        "Must have candidate consent for checks",
        "Don't make assumptions from social media",
        "DBS checks must be relevant to role",
        "Handle reference information confidentially"
      ]
    },

    "Job Offer & Contract": {
      guidance: `**Purpose:** Secure chosen candidate and formalize employment relationship.

**Offer Process:**
• Make verbal offer first (conditional)
• Confirm in writing
• Include salary, benefits, start date
• State conditions (references, right to work, etc.)
• Give reasonable time to accept (typically 5-7 days)
• Be prepared to negotiate

**Employment Contract:**
• Must be provided within 2 months of start
• Include all required terms (pay, hours, holidays, etc.)
• Clearly state probation period if applicable
• Include relevant policies
• Have reviewed by legal if needed

**Pre-Employment:**
• Complete right to work checks
• Arrange pre-employment screening
• Send welcome pack and first day information
• Prepare for onboarding

**Rejected Candidates:**
• Inform promptly and professionally
• Offer feedback if requested
• Keep details for future opportunities
• Maintain positive relationship`,
      tips: [
        "Move quickly - good candidates have other offers",
        "Be prepared to negotiate on salary/benefits",
        "Provide comprehensive offer letter",
        "Start onboarding process immediately"
      ],
      warnings: [
        "Don't make unconditional offer before checks complete",
        "Ensure contract terms match what was discussed",
        "Must complete right to work checks before start",
        "Poor rejection communication damages employer brand"
      ]
    }
  },

  // ONBOARDING WORKFLOW
  onboarding: {
    "Pre-Start Preparation": {
      guidance: `**Purpose:** Ensure everything is ready for new employee's first day.

**IT & Equipment:**
• Laptop/computer ordered and set up
• Email account created
• System access provisioned
• Phone/mobile if required
• Desk and workspace prepared

**Documentation:**
• Contract sent and signed
• Right to work documents verified
• Bank details collected
• Emergency contact information
• Starter checklist prepared

**Communication:**
• Welcome email from manager
• First day logistics (time, location, dress code)
• Parking/travel information
• What to bring on first day
• Who to ask for on arrival

**Team Preparation:**
• Team informed of new starter
• Buddy/mentor assigned
• Calendar invites sent
• Welcome lunch/coffee arranged`,
      tips: [
        "Start preparation 2 weeks before start date",
        "Send welcome pack before first day",
        "Test all IT equipment before arrival",
        "Assign a buddy for first few weeks"
      ],
      warnings: [
        "Nothing ready on day one creates terrible impression",
        "Don't assume IT will 'just work' - test it",
        "Lack of communication causes anxiety",
        "Team should know new person is starting"
      ]
    },

    "Day One Welcome & Setup": {
      guidance: `**Purpose:** Create positive first impression and get new employee set up.

**Morning:**
• Warm welcome from manager and team
• Office tour and facilities overview
• IT setup and system access
• Health and safety briefing
• Introduction to buddy/mentor

**Afternoon:**
• Overview of role and expectations
• Team introductions (if not done in morning)
• First tasks or reading materials
• Lunch with team or buddy
• Check-in at end of day

**Paperwork:**
• Complete any outstanding documentation
• Provide employee handbook
• Explain key policies
• Set up payroll and benefits

**Avoid:**
• Overwhelming with too much information
• Leaving them alone with no guidance
• Expecting productive work on day one
• Forgetting to introduce them to team`,
      tips: [
        "Have structured plan for first day",
        "Balance information with social integration",
        "Check in regularly throughout the day",
        "End day with positive feedback"
      ],
      warnings: [
        "Don't leave new starter sitting alone",
        "Avoid information overload - pace it out",
        "Manager must be available on day one",
        "Technical issues on day one are very demotivating"
      ]
    },

    "First Week Integration": {
      guidance: `**Purpose:** Help new employee understand role, team, and organization.

**Learning:**
• Detailed role overview and expectations
• Introduction to key systems and processes
• Understanding of team structure and dynamics
• Overview of current projects and priorities
• Company culture and values

**Relationships:**
• One-to-ones with key stakeholders
• Team meetings and social activities
• Regular check-ins with manager and buddy
• Building relationships with colleagues

**Early Tasks:**
• Small, achievable tasks to build confidence
• Shadowing team members
• Reading and learning materials
• Contributing to team discussions

**Support:**
• Daily check-ins with manager or buddy
• Clear point of contact for questions
• Patience with learning curve
• Positive feedback on progress`,
      tips: [
        "Set small, achievable goals for first week",
        "Schedule regular check-ins",
        "Encourage questions - no question is stupid",
        "Celebrate small wins"
      ],
      warnings: [
        "Don't expect full productivity in week one",
        "Avoid leaving them to 'figure it out'",
        "Don't overwhelm with complex tasks too soon",
        "Lack of support in first week increases early turnover"
      ]
    },

    "Month One Check-ins": {
      guidance: `**Purpose:** Monitor progress, address concerns, provide feedback.

**Weekly Check-ins:**
• How are they settling in?
• Any challenges or concerns?
• What support do they need?
• Feedback on performance so far
• Adjust onboarding plan if needed

**Topics to Cover:**
• Understanding of role and expectations
• Confidence with systems and processes
• Relationships with team and stakeholders
• Any training needs identified
• Work-life balance and wellbeing

**Manager Actions:**
• Provide constructive feedback
• Address any concerns promptly
• Adjust workload if needed
• Recognize progress and achievements
• Plan for month two and beyond

**Red Flags:**
• Struggling with basic tasks
• Not integrating with team
• Lack of engagement or enthusiasm
• Concerns about cultural fit
• Performance issues emerging`,
      tips: [
        "Schedule regular check-ins in advance",
        "Create safe space for honest feedback",
        "Be proactive in addressing concerns",
        "Recognize and celebrate progress"
      ],
      warnings: [
        "Don't ignore early warning signs",
        "Lack of feedback leaves employee uncertain",
        "Waiting until probation review is too late",
        "Address performance concerns early and supportively"
      ]
    },

    "Probation Review Process": {
      guidance: `**Purpose:** Formally assess performance and confirm permanent employment.

**Preparation:**
• Review performance against objectives
• Gather feedback from stakeholders
• Document achievements and concerns
• Prepare for honest discussion
• Schedule review meeting in advance

**Review Meeting:**
• Discuss performance against expectations
• Provide specific examples (positive and negative)
• Listen to employee's perspective
• Discuss development needs
• Decide on outcome (pass, extend, fail)

**Outcomes:**
• **Pass:** Confirm permanent employment
• **Extend:** Extend probation with clear objectives
• **Fail:** Terminate employment (with notice)

**Documentation:**
• Record review discussion
• Document decision and reasons
• Provide written confirmation
• File for employee record

**Legal Considerations:**
• Less than 2 years service = limited unfair dismissal rights
• But still must follow fair process
• Must not discriminate
• Document reasons for decision`,
      tips: [
        "Don't wait until review to raise concerns",
        "Be honest but constructive",
        "Focus on evidence, not opinions",
        "Give employee opportunity to respond"
      ],
      warnings: [
        "Failing probation without prior feedback is unfair",
        "Must follow own probation policy",
        "Can't extend probation indefinitely",
        "Even probationers have some employment rights"
      ]
    },

    "Long-term Integration": {
      guidance: `**Purpose:** Ensure sustained success and engagement beyond probation.

**Ongoing Development:**
• Regular one-to-ones continue
• Development plan created
• Training opportunities identified
• Career progression discussed
• Performance objectives set

**Integration:**
• Fully integrated into team
• Contributing to team goals
• Building wider networks
• Understanding organizational culture
• Taking on more responsibility

**Retention:**
• Regular feedback and recognition
• Development opportunities
• Work-life balance supported
• Engagement and satisfaction monitored
• Address any concerns promptly

**Measuring Success:**
• Performance meets expectations
• Employee is engaged and satisfied
• Positive relationships with team
• Contributing to team and organizational goals
• Likely to stay long-term`,
      tips: [
        "Don't stop supporting after probation",
        "Continue regular check-ins",
        "Provide development opportunities",
        "Recognize and reward good performance"
      ],
      warnings: [
        "Many employees leave in first year if not engaged",
        "Don't assume they're 'done' after probation",
        "Lack of development leads to disengagement",
        "Early turnover is costly and preventable"
      ]
    }
  },

  // PERFORMANCE REVIEW WORKFLOW
  performance: {
    "Objective Setting Phase": {
      guidance: `**Purpose:** Set clear, measurable objectives aligned to business goals.

**SMART Objectives:**
• Specific: Clear and unambiguous
• Measurable: Can track progress
• Achievable: Realistic given resources
• Relevant: Aligned to role and business goals
• Time-bound: Clear deadline

**Process:**
• Manager prepares draft objectives
• Employee reviews and provides input
• Collaborative discussion to agree objectives
• Document agreed objectives
• Link to development needs

**Number of Objectives:**
• Typically 3-5 key objectives
• Balance of business goals and development
• Mix of short-term and longer-term goals
• Include both 'what' and 'how' (behaviors)

**Cascading:**
• Align to team and organizational objectives
• Ensure line of sight to company strategy
• Avoid conflicting objectives across teams`,
      tips: [
        "Involve employee in setting their objectives",
        "Make objectives challenging but achievable",
        "Include development objectives, not just business targets",
        "Review and adjust objectives if circumstances change"
      ],
      warnings: [
        "Vague objectives are unfair and demotivating",
        "Too many objectives dilute focus",
        "Objectives imposed without discussion reduce buy-in",
        "Unrealistic objectives set people up to fail"
      ]
    },

    "Quarterly Check-in 1": {
      guidance: `**Purpose:** Review progress, provide feedback, adjust if needed.

**Discussion Topics:**
• Progress against each objective
• Challenges or obstacles encountered
• Support or resources needed
• Feedback on performance
• Any changes to priorities

**Manager Actions:**
• Provide specific, constructive feedback
• Recognize achievements
• Address any performance concerns
• Adjust objectives if business needs changed
• Identify development needs

**Employee Actions:**
• Update on progress
• Raise any concerns or challenges
• Request support if needed
• Reflect on own performance
• Identify learning needs

**Documentation:**
• Record discussion points
• Note any objective changes
• Document development needs
• File for year-end review`,
      tips: [
        "Don't wait for quarterly check-in to give feedback",
        "Focus on forward-looking discussion",
        "Be specific with examples",
        "Make it two-way conversation, not lecture"
      ],
      warnings: [
        "Skipping check-ins means no feedback until year-end",
        "Don't just focus on what's going wrong",
        "Avoid surprises - raise issues as they arise",
        "Check-ins shouldn't feel like interrogation"
      ]
    },

    "Mid-Year Review": {
      guidance: `**Purpose:** Formal review of progress at halfway point.

**Preparation:**
• Employee self-assessment
• Manager reviews performance evidence
• Gather feedback from stakeholders
• Review objectives and progress
• Prepare for honest discussion

**Review Meeting:**
• Discuss each objective and progress
• Provide balanced feedback (strengths and areas for improvement)
• Listen to employee's perspective
• Discuss development progress
• Adjust objectives for second half if needed
• Set development priorities

**Rating (if used):**
• Rate performance against objectives
• Use evidence and examples
• Be consistent across team
• Explain rating clearly
• Document rationale

**Outcomes:**
• Updated objectives for second half
• Development plan for remainder of year
• Any support or resources needed
• Documentation for year-end review`,
      tips: [
        "Prepare thoroughly - don't wing it",
        "Balance positive and developmental feedback",
        "Use specific examples, not generalizations",
        "Make it dialogue, not monologue"
      ],
      warnings: [
        "No surprises - employee should know how they're doing",
        "Don't rush the meeting",
        "Avoid recency bias - review full period",
        "Inconsistent ratings across team can indicate bias"
      ]
    },

    "Quarterly Check-in 2": {
      guidance: `**Purpose:** Continue regular feedback and support in second half of year.

**Focus Areas:**
• Progress since mid-year review
• Implementation of development plan
• Any new challenges or priorities
• Preparation for year-end review
• Continued feedback and support

**Discussion:**
• Review objectives and current progress
• Discuss any obstacles or concerns
• Provide ongoing feedback
• Recognize achievements
• Adjust plans if needed

**Looking Ahead:**
• Start thinking about year-end review
• Gather evidence of achievements
• Document examples for review
• Consider next year's objectives
• Plan for year-end discussion

**Development:**
• Review development plan progress
• Identify any additional learning needs
• Plan for remaining development activities
• Recognize skill improvements`,
      tips: [
        "Keep momentum going in second half",
        "Don't let feedback drop off",
        "Start preparing for year-end review",
        "Continue to recognize good performance"
      ],
      warnings: [
        "Second half often neglected - maintain focus",
        "Don't save all feedback for year-end",
        "Avoid letting performance drift",
        "Keep development plan active"
      ]
    },

    "Annual Review Process": {
      guidance: `**Purpose:** Comprehensive review of full year's performance.

**Preparation:**
• Employee completes self-assessment
• Manager reviews full year's evidence
• Gather 360-degree feedback if used
• Review all objectives and outcomes
• Prepare comprehensive feedback

**Review Meeting:**
• Discuss overall performance for year
• Review each objective and achievement
• Provide balanced, comprehensive feedback
• Discuss development progress
• Assign overall performance rating
• Discuss salary review (if linked)
• Look ahead to next year

**Performance Rating:**
• Rate against objectives and competencies
• Use full rating scale appropriately
• Be consistent across team
• Calibrate with other managers
• Document clear rationale

**Outcomes:**
• Overall performance rating
• Salary increase (if applicable)
• Bonus/reward (if applicable)
• Development plan for next year
• Career discussion
• Documentation for employee file`,
      tips: [
        "Review full year, not just recent months",
        "Be honest but constructive",
        "Recognize achievements genuinely",
        "Link performance to development needs"
      ],
      warnings: [
        "Recency bias is major risk - review whole year",
        "Ratings must be fair and consistent",
        "Don't rush this important conversation",
        "Surprises at year-end indicate poor ongoing feedback"
      ]
    },

    "Development Planning": {
      guidance: `**Purpose:** Create plan for employee's growth and development.

**Development Needs:**
• Skills gaps identified through review
• Career aspirations and goals
• Organizational needs and opportunities
• Strengths to leverage further
• Areas for improvement

**Development Activities:**
• Training courses (internal/external)
• Coaching or mentoring
• Stretch assignments or projects
• Job shadowing or secondments
• Self-directed learning
• Professional qualifications

**Development Plan:**
• Specific development objectives
• Activities to achieve objectives
• Timescales and milestones
• Resources required
• How progress will be measured
• Review dates

**Manager Support:**
• Provide opportunities for development
• Allocate time and resources
• Regular check-ins on progress
• Feedback and coaching
• Remove barriers to development`,
      tips: [
        "Make development plan specific and actionable",
        "Link to both performance and career goals",
        "Ensure resources are actually available",
        "Review and update plan regularly"
      ],
      warnings: [
        "Generic development plans are useless",
        "Don't create plan and then ignore it",
        "Lack of follow-through damages trust",
        "Development shouldn't only be about fixing weaknesses"
      ]
    }
  },

  // TRAINING PROGRAM WORKFLOW
  training: {
    "Training Needs Analysis": {
      guidance: `**Purpose:** Identify what training is needed and why.

**Analysis Methods:**
• Performance data and reviews
• Skills gap analysis
• Employee surveys and feedback
• Stakeholder interviews
• Compliance requirements
• Business strategy and future needs

**Questions to Answer:**
• What skills/knowledge are lacking?
• Who needs the training?
• Why is training needed? (business case)
• What will change after training?
• How will we measure success?
• What's the priority?

**Outputs:**
• Training needs identified
• Target audience defined
• Learning objectives specified
• Success criteria established
• Business case for training
• Budget requirements

**Stakeholder Involvement:**
• Managers and team leaders
• Employees (current and future needs)
• Senior leadership
• Subject matter experts
• L&D/HR team`,
      tips: [
        "Don't assume you know what's needed - ask",
        "Distinguish between training needs and other issues",
        "Prioritize based on business impact",
        "Consider different learning styles and needs"
      ],
      warnings: [
        "Training isn't always the answer to performance issues",
        "Skipping needs analysis leads to irrelevant training",
        "Don't train for training's sake",
        "Involve stakeholders early"
      ]
    },

    "Content Development": {
      guidance: `**Purpose:** Create effective training content aligned to learning objectives.

**Content Design:**
• Clear learning objectives
• Structured content flow
• Mix of theory and practice
• Real-world examples and scenarios
• Interactive elements
• Assessment/knowledge checks

**Delivery Methods:**
• Classroom/workshop
• E-learning/online modules
• Blended learning
• On-the-job training
• Coaching/mentoring
• Microlearning/just-in-time

**Materials:**
• Trainer guides and scripts
• Participant workbooks
• Presentations and visuals
• Activities and exercises
• Job aids and reference materials
• Assessment tools

**Accessibility:**
• Consider different learning styles
• Provide reasonable adjustments
• Use inclusive language and examples
• Ensure materials are accessible (fonts, colors, etc.)`,
      tips: [
        "Involve subject matter experts in content creation",
        "Use adult learning principles",
        "Make it practical and applicable",
        "Test content before full rollout"
      ],
      warnings: [
        "Death by PowerPoint - make it engaging",
        "Too much content overwhelms learners",
        "Theory without practice doesn't stick",
        "One-size-fits-all rarely works"
      ]
    },

    "Pilot Testing": {
      guidance: `**Purpose:** Test training with small group before full rollout.

**Pilot Group:**
• Representative sample of target audience
• Mix of roles, experience levels, locations
• Typically 10-20 participants
• Willing to provide honest feedback
• Available for full pilot session

**What to Test:**
• Content accuracy and relevance
• Delivery method effectiveness
• Timing and pacing
• Materials quality
• Trainer performance
• Learning outcomes achieved

**Feedback Collection:**
• Observation during session
• Participant feedback forms
• Post-training assessment
• Follow-up interviews
• Trainer reflection
• Stakeholder input

**Refinement:**
• Analyze feedback
• Identify improvements needed
• Revise content and materials
• Adjust delivery approach
• Re-test if major changes made`,
      tips: [
        "Choose diverse pilot group",
        "Create safe space for honest feedback",
        "Observe as well as ask for feedback",
        "Be prepared to make significant changes"
      ],
      warnings: [
        "Skipping pilot risks poor full rollout",
        "Don't ignore negative feedback",
        "Pilot with friendly audience only gives biased feedback",
        "Major issues found in pilot must be fixed"
      ]
    },

    "Full Deployment": {
      guidance: `**Purpose:** Roll out training to all target audience.

**Deployment Plan:**
• Schedule training sessions
• Book venues/set up online platform
• Communicate to participants
• Manage registrations
• Prepare trainers
• Distribute pre-work if needed

**Communication:**
• Explain purpose and benefits
• Clarify expectations
• Provide logistics (date, time, location)
• Send reminders
• Provide pre-work or preparation
• Set up for success

**Delivery:**
• Consistent delivery across all sessions
• Trained and prepared facilitators
• All materials and resources ready
• Technical setup tested
• Support available for issues
• Attendance tracked

**Participant Experience:**
• Welcoming and inclusive environment
• Engaging and interactive delivery
• Practical and relevant content
• Opportunity for questions
• Clear next steps
• Follow-up resources provided`,
      tips: [
        "Schedule sessions at convenient times",
        "Provide adequate notice",
        "Make attendance easy (location, timing)",
        "Ensure consistent quality across all sessions"
      ],
      warnings: [
        "Poor communication leads to low attendance",
        "Inconsistent delivery creates unfairness",
        "Technical issues damage credibility",
        "Mandatory training needs manager support"
      ]
    },

    "Progress Monitoring": {
      guidance: `**Purpose:** Track participation and early indicators of effectiveness.

**Participation Tracking:**
• Attendance/completion rates
• Drop-out rates (for longer programs)
• Time to complete (for e-learning)
• Engagement during sessions
• Completion of assessments
• Follow-up activities completed

**Early Indicators:**
• Immediate feedback (reaction)
• Knowledge assessments (learning)
• Confidence levels
• Intention to apply learning
• Early application on the job
• Manager observations

**Issues to Monitor:**
• Low attendance or completion
• Negative feedback
• Technical problems
• Accessibility issues
• Trainer performance concerns
• Content gaps identified

**Actions:**
• Follow up with non-attenders
• Address issues promptly
• Provide additional support if needed
• Adjust delivery based on feedback
• Communicate progress to stakeholders`,
      tips: [
        "Track metrics from the start",
        "Act on issues quickly",
        "Celebrate good participation",
        "Keep stakeholders informed"
      ],
      warnings: [
        "Low completion rates indicate problems",
        "Don't wait until end to identify issues",
        "Ignoring feedback damages credibility",
        "Poor monitoring means can't prove impact"
      ]
    },

    "Effectiveness Review": {
      guidance: `**Purpose:** Evaluate whether training achieved intended outcomes.

**Kirkpatrick's Four Levels:**
• **Level 1 - Reaction:** Did they like it?
• **Level 2 - Learning:** Did they learn?
• **Level 3 - Behavior:** Did they apply it?
• **Level 4 - Results:** Did it impact business?

**Data Collection:**
• Participant feedback surveys
• Knowledge assessments (pre/post)
• Manager observations of behavior change
• Performance data (KPIs, metrics)
• Business outcomes
• ROI calculation if appropriate

**Analysis:**
• Compare results to success criteria
• Identify what worked well
• Identify areas for improvement
• Assess value for money
• Determine if objectives achieved
• Gather testimonials and examples

**Reporting:**
• Summary of participation and completion
• Evidence of learning and application
• Business impact achieved
• Lessons learned
• Recommendations for future
• ROI if calculated

**Next Steps:**
• Decide on future rollouts
• Plan refresher training if needed
• Apply lessons to other training
• Celebrate success
• Address any gaps identified`,
      tips: [
        "Plan evaluation from the start",
        "Use multiple measures, not just feedback",
        "Give time for behavior change to occur",
        "Focus on business impact, not just activity"
      ],
      warnings: [
        "Measuring only 'happy sheets' is insufficient",
        "Behavior change takes time - don't assess too soon",
        "No evaluation means can't prove value",
        "Ignoring lessons learned wastes opportunity"
      ]
    }
  },

  // POLICY IMPLEMENTATION WORKFLOW
  policy: {
    "Policy Development": {
      guidance: `**Purpose:** Create clear, compliant policy that meets business needs.

**Needs Assessment:**
• Why is policy needed?
• What problem does it solve?
• What are legal requirements?
• What are current practices?
• What are the risks?

**Drafting:**
• Clear purpose and scope
• Plain English, avoid jargon
• Specific procedures and responsibilities
• Compliance with employment law
• Consistent with other policies
• Practical and enforceable

**Content Structure:**
• Purpose and scope
• Who policy applies to
• Key principles
• Detailed procedures
• Roles and responsibilities
• Monitoring and review
• Related policies and documents

**Legal Compliance:**
• Employment law requirements
• Data protection (GDPR)
• Health and safety
• Equality and discrimination
• Industry-specific regulations`,
      tips: [
        "Start with template but customize to your organization",
        "Involve people who will use the policy",
        "Keep it as simple as possible",
        "Focus on 'why' not just 'what'"
      ],
      warnings: [
        "Copying policies from internet without customization is risky",
        "Overly complex policies won't be followed",
        "Must comply with current law",
        "Policies that contradict practice create legal risk"
      ]
    },

    "Stakeholder Review": {
      guidance: `**Purpose:** Get input and buy-in from key stakeholders.

**Key Stakeholders:**
• Senior leadership (approval)
• Managers (will implement)
• Employees (will follow)
• HR team (will administer)
• Legal (compliance check)
• Trade unions (if applicable)

**Review Process:**
• Share draft policy
• Request feedback within timeframe
• Hold consultation meetings
• Address concerns and questions
• Revise based on feedback
• Confirm final approval

**Consultation Topics:**
• Is policy clear and understandable?
• Is it practical to implement?
• Are there unintended consequences?
• Does it address the need?
• Are there better alternatives?
• What support is needed for implementation?

**Managing Feedback:**
• Consider all feedback seriously
• Explain decisions (accept or reject)
• Find compromise where possible
• Document consultation process
• Build consensus and buy-in`,
      tips: [
        "Involve stakeholders early",
        "Allow adequate time for review",
        "Listen to implementation concerns",
        "Build support before launch"
      ],
      warnings: [
        "Imposing policy without consultation reduces buy-in",
        "Ignoring practical concerns leads to non-compliance",
        "Rushing review process misses important issues",
        "Lack of manager support undermines implementation"
      ]
    },

    "Legal Compliance Check": {
      guidance: `**Purpose:** Ensure policy complies with all legal requirements.

**Legal Review:**
• Employment law compliance
• Equality and discrimination law
• Data protection (GDPR)
• Health and safety requirements
• Industry-specific regulations
• Contractual implications

**Key Questions:**
• Does policy comply with current law?
• Are there any discriminatory elements?
• Does it protect employee rights?
• Does it protect organization from legal risk?
• Is it contractual or non-contractual?
• What are implications of non-compliance?

**Contractual vs Non-Contractual:**
• **Contractual:** Part of employment contract, harder to change
• **Non-Contractual:** Can be changed with notice
• Clearly state which it is
• Consider flexibility needed

**Documentation:**
• Legal review sign-off
• Record of legal advice
• Changes made based on legal review
• Risk assessment
• Implementation safeguards`,
      tips: [
        "Get legal review before implementation",
        "Be clear about contractual status",
        "Document legal advice received",
        "Update policy when law changes"
      ],
      warnings: [
        "Non-compliant policies create legal liability",
        "Contractual policies are hard to change later",
        "Assuming compliance without check is risky",
        "Outdated policies can breach current law"
      ]
    },

    "Communication Strategy": {
      guidance: `**Purpose:** Ensure all employees know about and understand new policy.

**Communication Plan:**
• Who needs to know?
• What do they need to know?
• When will we tell them?
• How will we communicate?
• Who will deliver the message?
• How will we check understanding?

**Communication Methods:**
• Email announcement
• Intranet/policy portal
• Team briefings
• All-hands meetings
• Posters/visual reminders
• FAQ documents
• Training sessions

**Key Messages:**
• Why policy is being introduced
• What it means for employees
• What's changing
• When it takes effect
• Where to find full policy
• Who to ask if questions

**Timing:**
• Announce before implementation
• Allow time for questions
• Provide notice of changes
• Reinforce after launch
• Ongoing reminders`,
      tips: [
        "Use multiple communication channels",
        "Explain 'why' not just 'what'",
        "Make it easy to access policy",
        "Encourage questions"
      ],
      warnings: [
        "Poor communication means people don't know about policy",
        "Assuming people read emails is risky",
        "One-off communication isn't enough",
        "Unclear messaging creates confusion"
      ]
    },

    "Staff Training & Rollout": {
      guidance: `**Purpose:** Ensure staff understand and can apply new policy.

**Training Needs:**
• Who needs training?
• What level of detail?
• Managers may need more detailed training
• Specific roles may need specialized training

**Training Content:**
• Purpose and rationale for policy
• Key principles and requirements
• Specific procedures to follow
• Roles and responsibilities
• Examples and scenarios
• Q&A session

**Training Delivery:**
• Face-to-face sessions for complex policies
• E-learning for simple policies
• Manager briefings
• Team meetings
• One-to-one for specific roles
• Written guidance and FAQs

**Manager Briefing:**
• Managers implement policies
• Need detailed understanding
• How to handle questions
• How to address non-compliance
• Support available

**Rollout:**
• Phased or all-at-once
• Pilot with one team first
• Monitor early implementation
• Address issues quickly
• Provide ongoing support`,
      tips: [
        "Don't assume policy is self-explanatory",
        "Provide practical examples",
        "Train managers first",
        "Make training engaging, not just reading policy"
      ],
      warnings: [
        "No training means policy won't be followed",
        "Complex policies need more than email",
        "Managers must understand policy to implement it",
        "Inconsistent understanding leads to inconsistent application"
      ]
    },

    "Implementation Monitoring": {
      guidance: `**Purpose:** Ensure policy is being followed and working as intended.

**Monitoring Methods:**
• Compliance audits
• Manager reports
• Employee feedback
• Incident tracking
• Grievances or complaints
• Performance data

**What to Monitor:**
• Is policy being followed?
• Are procedures being applied consistently?
• Are there implementation issues?
• Is policy achieving intended outcome?
• Are there unintended consequences?
• Do people understand the policy?

**Issues to Watch:**
• Non-compliance or inconsistent application
• Confusion or misunderstanding
• Practical barriers to compliance
• Negative impact on employees
• Legal or compliance risks
• Need for clarification or guidance

**Actions:**
• Address non-compliance
• Provide additional training if needed
• Clarify ambiguities
• Adjust policy if not working
• Communicate updates
• Recognize good compliance

**Review:**
• Scheduled policy reviews (annually)
• Review after incidents
• Review when law changes
• Review based on feedback
• Update as needed`,
      tips: [
        "Monitor from day one",
        "Act on issues quickly",
        "Be open to feedback",
        "Adjust if policy isn't working"
      ],
      warnings: [
        "Policy on paper but not in practice creates risk",
        "Inconsistent application can be discriminatory",
        "Ignoring feedback damages trust",
        "Outdated policies create compliance risk"
      ]
    }
  },

  // COMPLIANCE AUDIT WORKFLOW
  compliance: {
    "Audit Scope Definition": {
      guidance: `**Purpose:** Define what will be audited and why.

**Scope Considerations:**
• What compliance areas to audit?
• Which departments/locations?
• What time period?
• What depth of review?
• What resources available?
• What are the priorities?

**Compliance Areas:**
• Employment law compliance
• Health and safety
• Data protection (GDPR)
• Equality and diversity
• Right to work
• Working time regulations
• Pay and benefits
• Record keeping

**Risk-Based Approach:**
• Focus on high-risk areas first
• Consider previous audit findings
• Recent legal changes
• Known issues or concerns
• Industry-specific requirements

**Audit Plan:**
• Objectives and scope
• Timeline and milestones
• Resources and team
• Methodology
• Reporting requirements
• Stakeholder communication`,
      tips: [
        "Be realistic about scope - don't try to audit everything",
        "Prioritize based on risk",
        "Involve stakeholders in scoping",
        "Set clear objectives"
      ],
      warnings: [
        "Too broad a scope dilutes effectiveness",
        "Ignoring high-risk areas is dangerous",
        "Inadequate resources lead to superficial audit",
        "Lack of clear scope causes scope creep"
      ]
    },

    "Data Collection": {
      guidance: `**Purpose:** Gather evidence of compliance or non-compliance.

**Data Sources:**
• Policies and procedures
• Employee records and files
• System data and reports
• Documents and forms
• Training records
• Incident reports
• Previous audit findings

**Collection Methods:**
• Document review
• System audits
• File sampling
• Interviews with staff
• Observations
• Questionnaires/surveys

**Sampling:**
• Random sampling for large populations
• Targeted sampling for high-risk areas
• Sufficient sample size for confidence
• Document sampling methodology
• Ensure representative sample

**Evidence:**
• Collect objective evidence
• Document findings contemporaneously
• Maintain audit trail
• Store securely
• Respect confidentiality

**Stakeholder Engagement:**
• Inform relevant managers
• Request documents and access
• Schedule interviews
• Explain audit process
• Maintain professional approach`,
      tips: [
        "Be systematic and thorough",
        "Document everything",
        "Maintain objectivity",
        "Respect confidentiality"
      ],
      warnings: [
        "Incomplete data leads to incomplete findings",
        "Biased sampling skews results",
        "Poor documentation undermines credibility",
        "Breaching confidentiality damages trust"
      ]
    },

    "Gap Analysis": {
      guidance: `**Purpose:** Identify gaps between current state and compliance requirements.

**Analysis Process:**
• Compare current practice to requirements
• Identify areas of non-compliance
• Assess severity and risk of each gap
• Determine root causes
• Prioritize gaps for remediation

**Gap Categories:**
• **Critical:** Immediate legal risk, must fix urgently
• **High:** Significant risk, fix within 30 days
• **Medium:** Moderate risk, fix within 90 days
• **Low:** Minor risk, fix within 6 months

**Root Cause Analysis:**
• Why does gap exist?
• Is it lack of knowledge?
• Lack of resources?
• System limitations?
• Policy gaps?
• Cultural issues?

**Risk Assessment:**
• What's the legal risk?
• What's the financial risk?
• What's the reputational risk?
• What's the likelihood of issue arising?
• What's the potential impact?

**Documentation:**
• List all gaps identified
• Categorize by severity
• Document evidence
• Explain root causes
• Assess risks`,
      tips: [
        "Be honest about gaps - don't hide issues",
        "Focus on root causes, not symptoms",
        "Prioritize based on risk",
        "Involve subject matter experts"
      ],
      warnings: [
        "Downplaying gaps increases risk",
        "Focusing only on easy fixes ignores serious issues",
        "Not understanding root cause leads to ineffective solutions",
        "Poor prioritization wastes resources"
      ]
    },

    "Remediation Planning": {
      guidance: `**Purpose:** Create action plan to address identified gaps.

**Remediation Plan:**
• Specific actions to address each gap
• Owner responsible for each action
• Realistic timescales
• Resources required
• Success criteria
• Dependencies

**Action Types:**
• Policy updates
• Process changes
• System improvements
• Training delivery
• Communication
• Monitoring enhancements

**Prioritization:**
• Critical gaps first
• Quick wins for momentum
• Complex issues need more time
• Balance urgency with feasibility

**Resource Planning:**
• Budget required
• People resources
• Time commitment
• External support needed
• Technology or systems

**Stakeholder Buy-in:**
• Present findings and plan
• Explain risks and priorities
• Get approval and resources
• Assign clear ownership
• Set expectations`,
      tips: [
        "Be realistic about timescales",
        "Assign clear ownership",
        "Get senior leadership buy-in",
        "Plan for resources needed"
      ],
      warnings: [
        "Plans without resources won't be implemented",
        "Unrealistic timescales lead to failure",
        "Lack of ownership means nothing happens",
        "Not addressing critical gaps is negligent"
      ]
    },

    "Implementation Phase": {
      guidance: `**Purpose:** Execute remediation plan and close compliance gaps.

**Implementation:**
• Execute actions according to plan
• Track progress against milestones
• Address obstacles and issues
• Adjust plan if needed
• Communicate progress
• Document completion

**Project Management:**
• Regular status updates
• Risk and issue management
• Change control
• Stakeholder communication
• Resource management
• Quality assurance

**Common Actions:**
• Update policies and procedures
• Deliver training programs
• Implement system changes
• Improve processes
• Enhance documentation
• Strengthen controls

**Monitoring:**
• Track completion of actions
• Verify effectiveness of changes
• Gather evidence of compliance
• Address any new issues
• Report progress to stakeholders

**Challenges:**
• Competing priorities
• Resource constraints
• Resistance to change
• Technical difficulties
• Scope creep`,
      tips: [
        "Maintain momentum - don't let it drag",
        "Communicate progress regularly",
        "Celebrate quick wins",
        "Be flexible but focused"
      ],
      warnings: [
        "Implementation often stalls - maintain focus",
        "Partial implementation doesn't achieve compliance",
        "Lack of follow-through wastes audit effort",
        "Changing priorities shouldn't derail critical fixes"
      ]
    },

    "Follow-up Review": {
      guidance: `**Purpose:** Verify that remediation actions achieved compliance.

**Review Activities:**
• Verify all actions completed
• Test effectiveness of changes
• Gather evidence of compliance
• Identify any remaining gaps
• Assess sustainability of improvements

**Verification Methods:**
• Document review
• Process testing
• Interviews and observations
• Data analysis
• Spot checks
• Re-audit of specific areas

**Outcomes:**
• **Compliant:** Gap closed, no further action
• **Partially compliant:** Some improvement, more work needed
• **Non-compliant:** Action ineffective, new approach needed

**Reporting:**
• Summary of remediation actions
• Evidence of compliance achieved
• Any remaining gaps
• Lessons learned
• Recommendations for ongoing compliance
• Next audit date

**Continuous Improvement:**
• Embed compliance in BAU
• Regular monitoring
• Ongoing training
• Policy reviews
• Culture of compliance`,
      tips: [
        "Don't assume actions worked - verify",
        "Be thorough in verification",
        "Document evidence of compliance",
        "Plan for ongoing compliance"
      ],
      warnings: [
        "Declaring success without verification is risky",
        "One-time fixes often don't stick",
        "Compliance is ongoing, not one-off",
        "Lessons learned should inform future audits"
      ]
    }
  }
};

/**
 * Get workflow-level guidance for overview
 */
export const getWorkflowTypeGuidance = (workflowType) => {
  return workflowTypeGuidance[workflowType] || null;
};

/**
 * Get step-level guidance
 */
export const getWorkflowStepGuidance = (workflowType, stepName) => {
  return workflowStepGuidance[workflowType]?.[stepName] || null;
};
